// @flow
import { MnObject } from "backbone.marionette";
import startsWith from "lodash-es/startsWith";
import Backbone from "extensions/backbone";
import { getSsoLogoutUrl } from "merchant/features/userManagement/sso_helper";
import moment from "moment";
import { Crypt } from "common/components/crypt";
import { captureBreadcrumb } from "extensions/sentry";

import Radio from "backbone.radio";

import { onVisibilityChange, removeURLParameter } from "common/utils";
import { store } from "common/store";

import { CLEAR_HISTORY_STACK } from "common/actions/common";

import { pazienHistory } from "common/components/pazienHistory";

type ClearLocalStorageOptionsType = {
    except: Array<string>
};

type LogoutOptionsType = {
    rememberLastUrl: boolean
};

// Type of a value stored in LocalStorage
type StoredValueType = string | null;

type TokenExpirationTimeType = string | number;

export const KEYS = {
    SLT: "slt",
    TOKEN: "token",
    EXPIRES: "token-expiresAt",
    LAST_FRAGMENT_MERCHANT: "last_fragment_merchant",
    LAST_FRAGMENT_ADMIN: "last_fragment_admin",
    FEATURES_DEV: "features-dev",
    FEATURES: "features",
    AUTHENTICATION_INFO: "authentication_info"
};

const persistent_key = "auth-info";

const Security = MnObject.extend({
    /**
     * Set security radio channel
     * Events that this channel fires:
     * - forcePasswordChange
     * - showSessionTimeout
     * - loginSuccess
     * - logoutComplete
     */
    channelName: "security",
    
    radioEvents: {
        loginSuccess: "loginSuccess"
    },
    
    radioRequests: {
        "user:authenticate": "authenticate",
        "user:isLoggedIn": "isLoggedIn",
        "user:forcePasswordChange": "forcePasswordChange",
        "user:logout": "logout",
        "user:get:authInfo": "getUserAuthInfo",
        "user:set:authInfo": "setUserAuthInfo",
        "system:clean": "_cleanUp",
        "system:force:login:page": "forceShowLoginPage",
        "token:get": "getToken",
        "token:get:expiration": "getExpiration",
        "lastUrl:get": "getLastUrl",
        "lastUrl:set": "setLastUrl"
    },
    
    initialize() {
        // Every time window becomes focused check if user should be logged in according to security module,
        // but is not according to data in localStorage
        onVisibilityChange((visible: boolean) => {
            if (visible) {
                this.onPageVisible();
            } else {
                this.onPageHidden();
            }
        });
    },
    
    shouldReloadUnprotectedRoutes: true,

    onPageVisible() {
        const isProtectedRoute = Backbone.history.fragmentOptions?.ensureAuth === true;
        if (this.getToken() === null && !isProtectedRoute) return;
        
        if (this.getExpiration() < Date.now()) {
            captureBreadcrumb({
                message: "Force logout of a user",
                category: "security",
                data: {
                    now: moment().toISOString(),
                    expirationISO: moment(this.getExpiration()).toISOString()
                }
            });
            this.logout({
                rememberLastUrl: true
            });
        } else if (!isProtectedRoute && this.shouldReloadUnprotectedRoutes) {
            window.location.reload();
        } else {
            // Restore notification timer
            this.scheduleSessionTimeoutWarning();
        }
    
        this.getChannel().trigger("onPageVisible");
    },
    
    onPageHidden() {
        this.getChannel().trigger("onPageHidden");
        this.clearNotificationTimer();
    },
    
    /**
     * Force password change.
     * Fires request that api requires change of the existing password
     * @param token
     * @param expiresAt
     * @param passExpiresInDays
     */
    forcePasswordChange(token: string, expiresAt: TokenExpirationTimeType, passExpiresInDays: number) {
        if (token) {
            this.getChannel().trigger("forcePasswordChange", token, expiresAt, passExpiresInDays);
        }
    },
    
    /**
     * Get current token expiration time
     * @returns {number}
     */
    getExpiration(): number {
        const expirationString = localStorage.getItem(KEYS.EXPIRES);
        
        if (expirationString) {
            return parseInt(expirationString, 10);
        }
    
        return 0;
    },
    
    /**
     * Set token expiration
     * @param tokenExpiresAt
     */
    setExpiration(tokenExpiresAt: StoredValueType) {
        if (tokenExpiresAt === null) {
            localStorage.removeItem(KEYS.EXPIRES);
        } else {
            localStorage.setItem(KEYS.EXPIRES, tokenExpiresAt);
        }
    },
    
    /**
     * Get token of currently logged in user.
     * @returns {*}
     */
    getToken(): StoredValueType {
        const encrypted = localStorage.getItem(KEYS.TOKEN);
        if (encrypted) {
            try {
                return Crypt.decipher(encrypted);
            } catch (err) {
                // clear local storage at this point and ask user to re-login.
                this._cleanUp();
                return null;
            }
        }
        
        return null;
    },
    
    /**
     * Set current token
     * @param token
     */
    setToken(token: StoredValueType) {
        if (token === null) {
            captureBreadcrumb({
                message: "Removing a token from LocalStorage",
                category: "security"
            });
            
            localStorage.removeItem(KEYS.TOKEN);
        } else {
            const encrypted = Crypt.cipher(token);
            captureBreadcrumb({
                message: "Setting token to LocalStorage",
                category: "security"
            });
            
            localStorage.setItem(KEYS.TOKEN, encrypted);
        }
    },
    
    /**
     * Authenticate the user with passed in params
     * @param token
     * @param expiresAt
     */
    authenticate(token: string, expiresAt: TokenExpirationTimeType = moment().add(15, "minutes").valueOf()): boolean {
        const tokenExpiresAtUnixTime = +new Date(expiresAt) - this.gracePeriod;
        const previousExpiresAtUnixTime = this.getExpiration();

        // Return earlier if the same (or earlier) expiration date is already set and there is no token to set
        if (tokenExpiresAtUnixTime <= previousExpiresAtUnixTime && !token) {
            return false;
        }
        
        this.setExpiration(tokenExpiresAtUnixTime);
        this.scheduleSessionTimeoutWarning();
        
        if (token) {
            // If there was a token then set the token and trigger login success request
            this.setToken(token);
            CLEAR_HISTORY_STACK();
            this.getChannel().trigger("loginSuccess");
        }
        
        return true;
    },
    
    timeoutThreshold: 2 * 60 * 1000,
    
    gracePeriod: 10 * 1000, // 10 seconds grace period to prevent 401s due to request taking time and time-sync issues between API nodes
    
    scheduleSessionTimeoutWarning() {
        this.getChannel().trigger("scheduleSessionTimeout");
    
        // Clear notification timer
        this.clearNotificationTimer();
    
        // Schedule sessionTimeout dialog to show up
        const showPopupInMs = this.getExpiration() - Date.now() - this.timeoutThreshold;
        this.notificationTimer = setTimeout(() => {
            // Before firing the event we should check if the token is REALLY set to expire in `${showPopupInMs}`ms.
            // Another tab or browser window could update token expiration time; however, this app instance (and security module instance) still
            // expect old expiration time.
            if (this.getExpiration() - Date.now() <= this.timeoutThreshold) {
                this.getChannel().trigger("showSessionTimeout");
            } else {
                // Schedule another warning if
                this.getChannel().trigger("rescheduleSessionTimeout");
                this.scheduleSessionTimeoutWarning();
            }
        }, showPopupInMs);
    },
    
    loginSuccess() {
        captureBreadcrumb({
            message: "Login successful",
            category: "security"
        });
        
        // Go to currently selected route or the default one
        const url = this.getLastUrl();
        if (url) {
            // Nullify entry in localStorage
            this.setLastUrl(null);
            // Navigate to the url without any params
            pazienHistory.navigate(url, { trigger: true, replace: true });
        } else {
            // Temporarily disable reloading unprotected routes while the user navigates to their start page.
            // This prevents the merchant picker from triggering reload of the login page when window focus is lost.
            this.shouldReloadUnprotectedRoutes = false;
            return Promise.resolve(Radio.channel("main").request("get:user").fetchPrerequisites.then(() => {
                // Go to a default url with stored params like merchant.
                pazienHistory.navigateWithParams(
                    store.getState().user_merchant_settings.start_page || store.getState().config.defaultRouteMerchant,
                    { trigger: true, replace: true }
                );
                // Fine to reload routes after we've successfully logged the user in.'
                this.shouldReloadUnprotectedRoutes = true;
            }));
        }
    },
    
    /**
     * Check if token is set and not expired and we can treat user as Logged In
     * @returns {boolean}
     */
    isLoggedIn(): boolean {
        return this.getToken() !== null && this.getExpiration() > Date.now();
    },
    
    logout({ rememberLastUrl = false }: LogoutOptionsType) {
        this.getChannel().trigger("logout:start");
    
        const oldToken = this.getToken();
        const state = store.getState();
        let logoutUrl = state.config.security.loginMerchant;
        if (state.common.user.sso_user === true) {
            logoutUrl = getSsoLogoutUrl() || logoutUrl;
        }
        
        if (rememberLastUrl) {
            this.setLastUrl(Backbone.history.fragment);
            
            // If user is not sso user and we just stored last url, then pass param to the logout url
            if (!state.common.user.sso_user) {
                logoutUrl = `${logoutUrl}?timeout=true`;
            }
        }
        
        this._cleanUp();
        this.getChannel().trigger("logoutComplete", { oldToken, logoutUrl });
    },
    
    clearNotificationTimer() {
        if (this.notificationTimer) {
            clearTimeout(this.notificationTimer);
            this.notificationTimer = null;
        }
    },
    
    /**
     * Clean up localstorage, timers, etc.
     * @returns {boolean}
     */
    _cleanUp() {
        this.clearLocalStorage({
            except: [
                KEYS.LAST_FRAGMENT_MERCHANT,
                KEYS.LAST_FRAGMENT_ADMIN,
                KEYS.FEATURES_DEV,
                KEYS.FEATURES,
                KEYS.AUTHENTICATION_INFO,
                "settings",
                "settings_dev",
                "recent-merchants",
                "default_report"
            ]
        });
        
        this.clearNotificationTimer();
    },
    
    /**
     * Clean up localStorage except passed in keys
     * @param {Array} [options.except] an array of items to keep in localStorage
     */
    clearLocalStorage({ except = [] }: ClearLocalStorageOptionsType) {
        // When you remove an item, the length changes. You should iterate backwards
        for (let i = localStorage.length; i >= 0; i--) {
            const key = localStorage.key(i);
            if (typeof key === "string" && !except.includes(key) && !startsWith(key, "_BEAMER")) { // Preserve Beamer keys in the storage
                localStorage.removeItem(key);
            }
        }
    },
    
    /**
     * Get remembered last url
     * @returns {String} last url
     */
    getLastUrl(): StoredValueType {
        const KEY = KEYS.LAST_FRAGMENT_MERCHANT;
        
        // Check if we have last url stored somewhere in local storage
        const encrypted = localStorage.getItem(KEY);
        let decrypted = null;
        
        if (encrypted) {
            try {
                decrypted = Crypt.decipher(encrypted, persistent_key);
            } catch (err) {
                // User-agent has changed and we cannot decipher last url
                // Clean up and return undefined
                localStorage.removeItem(KEY);
            }
        }
        
        return decrypted;
    },
    
    /**
     * Remember last url used by the user
     * @param {String | null} lastUrl
     */
    setLastUrl(lastUrl = pazienHistory.getCurrentFragment(), { removeDatesFromURL = true } = {}) {
        const KEY = KEYS.LAST_FRAGMENT_MERCHANT;
        
        if (lastUrl === null) {
            localStorage.removeItem(KEY);
        }
        
        // Don't remember default route or login/reset/saml kinda urls
        if (lastUrl === store.getState().config.defaultRouteMerchant || ["login", "logout", "reset", "new_user", "saml"].some((r: string): boolean => startsWith(lastUrl, r))) {
            return;
        }
        
        if (typeof lastUrl !== "string") return;
    
        let url = lastUrl;
        if (removeDatesFromURL) {
            url = removeURLParameter({
                url: lastUrl,
                parameter: "dates"
            });
        }
    
        const encrypted = Crypt.cipher(url, persistent_key);
        
        captureBreadcrumb({
            message: "Remembering last url",
            category: "security",
            data: { url }
        });
    
        localStorage.setItem(KEY, encrypted);
    },

    forceShowLoginPage() {
        this.logout({ rememberLastUrl: false });
    },

    /**
     * Get authentication info that we can use for this user based on user's past activity
     */
    getUserAuthInfo(): StoredValueType {
        // Check if we have auth info stored somewhere in local storage
        const encrypted = localStorage.getItem(KEYS.AUTHENTICATION_INFO);
        let decrypted = null;

        if (encrypted) {
            try {
                // We can't use random salt for auth info as we persist the value after user logs out
                decrypted = Crypt.decipher(encrypted, persistent_key);
            } catch (err) {
                // User-agent has changed and we cannot decipher auth info
                // Clean up and return null
                localStorage.removeItem(KEYS.AUTHENTICATION_INFO);
            }
        }

        return decrypted;
    },

    setUserAuthInfo(authInfo: StoredValueType) {
        if (authInfo === null) {
            localStorage.removeItem(KEYS.AUTHENTICATION_INFO);
        } else {
            localStorage.setItem(KEYS.AUTHENTICATION_INFO, Crypt.cipher(authInfo, persistent_key));
        }
    }
});

export const security = new Security();
